import type RouterService from "@ember/routing/router-service";
import Service, { service } from "@ember/service";
import type Store from "ember-data/store";
import type errors from "parog-web/services/errors";
import type SemeiaSessionService from "./session";

/**
 * Ordoclic service to handle integration with Ordoclic
 *
 * This service manages the computation of the iframe URL.
 * We can retrieve the iframe URL using the `computeOrdoclicPrescriptionIframe` method that return the url computed.
 *
 * It also listens to messages from the iframe to handle prescriptions creation
 * and iframe closing.
 *
 */
export default class OrdoclicService extends Service {
  @service declare errors: errors;
  @service declare requestManager;
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  private isListeningIframe = false;

  private get isEnabled() {
    return this.session.medic?.lgcEnabled;
  }

  async computeOrdoclicPrescriptionIframe(patientId: string): Promise<string> {
    if (!this.isEnabled) return "";
    if (!this.isListeningIframe) this.listenIframeEvents();

    return await this.getIframeUrl(patientId);
  }

  private async createJournalEventFromOrdoclic(
    isPrescription: boolean,
    ordoclicId: string
  ) {
    try {
      const url = isPrescription
        ? `/api/v1/ordoclic/synchronize_prescription/${ordoclicId}`
        : `/api/v1/ordoclic/synchronize_document/${ordoclicId}`;

      const response = await this.requestManager.request({
        url,
        method: "POST"
      });
      this.store.pushPayload(response.content);
    } catch (e) {
      console.error("Error handling prescription:", e);
      this.errors.displayError(
        `Une erreur est survenue lors de la création de la prescription`
      );
    }
  }

  private async getIframeUrl(patientId: string) {
    try {
      const response = await this.requestManager.request({
        url: `/api/v1/ordoclic/iframe_url/${patientId}`,
        method: "GET"
      });
      return response.content.url;
    } catch (e) {
      console.error("Error handling prescription:", e);
      this.errors.displayError(
        `Une erreur est survenue lors du chargement d'Ordoclic`
      );
    }
  }

  private listenIframeEvents() {
    if (!this.isEnabled) return;
    if (this.isListeningIframe) return;
    window.addEventListener("message", this.onIframeMessage);
    this.isListeningIframe = true;
  }

  private onIframeMessage = async event => {
    switch (event.data?.type) {
      case "batch-created": {
        const batch = event.data?.message?.batch ?? [];
        const tasks = batch.map(({ isPrescription, id }) =>
          this.createJournalEventFromOrdoclic(isPrescription, id)
        );
        await Promise.allSettled(tasks);
        break;
      }

      case "close-iframe":
        this.router.transitionTo(
          "medics-web.protected.patients.patient.dashboard.generic-medical-data"
        );
        break;
    }
  };
}
